import { useEffect } from 'react';
import { PRO_ENABLED } from '@/lib/features';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Home, Compass, Bookmark, Clock, Settings, Plus, Flame, LogOut, Users, Download, Crown, Trophy, Bell, Rocket, LogIn } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore } from '@/stores/ui-store';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { trackProUpgradeClick, trackUpgradePromptShown, trackProUpsellView, trackProCtaImpression, trackProCtaClick, trackStreakUpdated } from '@/lib/analytics';
import { GaragePulseCard } from '@/components/widgets/GaragePulseCard';
import { StreakBadge } from '@/components/widgets/StreakBadge';

// T-127: `authed: true` means the item is meaningless to a signed-out visitor —
// it either 401s or shows an empty personal list. Public items stay visible so a
// stranger can still browse; that is the whole point of the anonymous homepage.
const navItems = [
  { path: '/', label: 'My Feed', icon: Home, authed: false },
  { path: '/explore', label: 'Explore', icon: Compass, authed: false },
  { path: '/notifications', label: 'Notifications', icon: Bell, authed: true },
  { path: '/garages', label: 'Garages', icon: Users, authed: false },
  { path: '/launches', label: 'Launches', icon: Rocket, authed: false },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, authed: false },
  { path: '/bookmarks', label: 'Bookmarks', icon: Bookmark, authed: true },
  { path: '/history', label: 'History', icon: Clock, authed: true },
  { path: '/settings', label: 'Settings', icon: Settings, authed: true },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed } = useUiStore();
  // T-127: read the SAME field the header reads. The header gates on
  // `isAuthenticated`; deriving a second answer from `!!user` here is how one
  // paint ended up showing 'Sign in' and 'Sign out' at the same time.
  const { user, isAuthenticated, logout } = useAuthStore();
  const { canInstall, install } = usePwaInstall();

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const { data: myGarages } = useQuery({
    queryKey: ['garages', 'my'],
    queryFn: () => api.garages.my(),
    enabled: isAuthenticated,
  });

  // ENGAGE-001: Bookmark count badge in nav
  const { data: bookmarkCountData } = useQuery({
    queryKey: ['bookmarks', 'count'],
    queryFn: () => api.feed.bookmarksCount(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // GROWTH-003: Notification unread count badge in nav
  const { data: notifCountData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const { data: streak } = useQuery({
    queryKey: ['streak'],
    queryFn: () => api.users.getStreak(),
    enabled: isAuthenticated,
  });

  const queryClient = useQueryClient();
  const { mutate: recordActivity } = useMutation({
    mutationFn: () => api.users.recordActivity(),
    onSuccess: (data) => {
      const today = new Date().toISOString().slice(0, 10);
      sessionStorage.setItem('streak_recorded_today', today);
      queryClient.invalidateQueries({ queryKey: ['streak'] });
      // ANALYTICS-050: fire streak_updated when streak changes
      if (data && data.currentStreak > 0) {
        const prevStreak = streak?.currentStreak ?? 0;
        if (data.currentStreak !== prevStreak) {
          trackStreakUpdated(data.currentStreak, data.currentStreak > (data.longestStreak - 1));
        }
      }
    },
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    const today = new Date().toISOString().slice(0, 10);
    if (sessionStorage.getItem('streak_recorded_today') !== today) {
      recordActivity();
    }
  }, [recordActivity, isAuthenticated]);

  // ANALYTICS-006: Track sidebar upgrade prompt impression (once per session)
  // ANALYTICS-031: also emit unified funnel-stage view event for the funnel report.
  useEffect(() => {
    if (PRO_ENABLED && user && !user.isPro && !sessionStorage.getItem('mw_sidebar_upgrade_seen')) {
      sessionStorage.setItem('mw_sidebar_upgrade_seen', '1');
      trackUpgradePromptShown('sidebar');
      trackProUpsellView('sidebar');
      // ANALYTICS-044: page-level baseline event for cross-surface CTR baseline.
      trackProCtaImpression('sidebar');
    }
  }, [user]);

  if (sidebarCollapsed) {
    return null;
  }

  return (
    <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-56 flex-shrink-0 overflow-y-auto border-r border-border-primary p-4 lg:block">
      {/* Navigation */}
      <nav className="space-y-1">
        {navItems.filter((item) => isAuthenticated || !item.authed).map((item) => {
          const isActive = location.pathname === item.path;
          const bookmarkCount = item.path === '/bookmarks' ? (bookmarkCountData?.count ?? 0) : 0;
          const notifCount = item.path === '/notifications' ? (notifCountData?.count ?? 0) : 0;
          const badgeCount = bookmarkCount || notifCount;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-500/10 text-primary-500'
                  : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {badgeCount > 0 && (
                <span className="ml-auto rounded-full bg-bg-tertiary px-2 py-0.5 text-xs font-semibold text-text-tertiary tabular-nums">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Streak Widget + ENGAGE-005 milestone badge */}
      {streak && streak.currentStreak > 0 && (
        <div className="mt-6 rounded-xl border border-border-primary bg-bg-secondary p-3">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-streak" />
            <div className="flex-1">
              <div className="text-lg font-bold text-text-primary">{streak.currentStreak}</div>
              <div className="text-xs text-text-tertiary">day streak</div>
            </div>
            <StreakBadge
              currentStreak={streak.currentStreak}
              longestStreak={streak.longestStreak ?? streak.currentStreak}
              size="sm"
              showLabel={false}
            />
          </div>
        </div>
      )}

      {/* UI-093: Garage Pulse — community activity summary */}
      <GaragePulseCard />

      {/* My Garages — T-127: a signed-out visitor has none, and /garages/new 401s. */}
      {isAuthenticated && (
      <div className="mt-6">
        <div className="flex items-center justify-between px-3">
          <h3 className="text-[14px] font-bold uppercase tracking-[0.4px] text-text-tertiary">
            My Garages
          </h3>
          <Link
            to="/garages/new"
            className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary"
          >
            <Plus className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-2 space-y-1">
          {myGarages?.items.slice(0, 5).map((garage) => (
            <Link
              key={garage.id}
              to={`/garage/${garage.slug}`}
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-sm">
                {garage.emoji || '🚗'}
              </div>
              <span className="truncate">{garage.name}</span>
            </Link>
          ))}

          {(!myGarages || myGarages.items.length === 0) && (
            <p className="px-3 text-xs text-text-tertiary">No garages yet</p>
          )}
        </div>
      </div>
      )}

      {/* Go Pro CTA — T-120: hidden while Pro is uncommissioned (decision 105). */}
      {PRO_ENABLED && user && !user.isPro && (
        <div className="mt-6">
          <Link
            to="/pro"
            onClick={() => { trackProUpgradeClick('sidebar'); trackProCtaClick('sidebar'); }}
            className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/20 transition-colors"
          >
            <Crown className="h-5 w-5" />
            <span className="flex-1">Go Pro</span>
            <span className="text-xs font-normal text-amber-300/80">&#8377;149/mo</span>
          </Link>
        </div>
      )}

      {/* PWA Install Prompt */}
      {canInstall && (
        <div className="mt-6">
          <button
            onClick={install}
            className="flex w-full items-center gap-3 rounded-lg border border-primary-500/30 bg-primary-500/10 px-3 py-2 text-sm font-medium text-primary-400 hover:bg-primary-500/20"
          >
            <Download className="h-5 w-5" />
            Install App
          </button>
        </div>
      )}

      {/* T-127: the sidebar must offer the SAME action the header offers. */}
      <div className="mt-auto pt-6">
        {isAuthenticated ? (
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
          >
            <LogOut className="h-5 w-5" />
            Sign out
          </button>
        ) : (
          <div className="space-y-1">
            <Link
              to="/auth/login"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
            >
              <LogIn className="h-5 w-5" />
              Sign in
            </Link>
            <Link
              to="/auth/register"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              Join
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
